(() => {
  'use strict';
  class MCaseModulesShop extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.selection = new Map();
      this.busy = false;
      this.abort = new AbortController();
      this.runtime = JSON.parse(this.querySelector('[data-modules-runtime]').textContent);
      this.strings = this.runtime.strings;
      this.initialize();
    }
    disconnectedCallback() {
      this.abort?.abort();
    }
    escape(value) {
      return String(value).replace(/[&<>"']/g, char => ({ '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;' })[char]);
    }
    message(key, values = {}) {
      return Object.entries(values).reduce((text, [name, value]) => text.replaceAll(`{${name}}`, value), this.strings[key] || key);
    }
    money(cents) {
      return this.runtime.currency === 'USD' ? `US$${(cents / 100).toFixed(2)}` : new Intl.NumberFormat(undefined, { style:'currency', currency:this.runtime.currency, currencyDisplay:'code' }).format(cents / 100);
    }
    status(text = '', error = false) {
      const node = this.querySelector('[data-modules-status]');
      node.textContent = text;
      node.classList.toggle('is-error', error);
    }
    async initialize() {
      try {
        const response = await fetch(this.dataset.catalogUrl, { signal:this.abort.signal });
        if (!response.ok) throw new Error(this.message('load_error'));
        this.catalog = await response.json();
        const live = new Map(this.runtime.variants.map(variant => [variant.sku, variant]));
        this.choices = new Map(this.catalog.variants.map(source => {
          const variant = live.get(source.sku);
          const module = this.catalog.parts.find(part => part.id === source.key.split(':')[0]);
          const color = this.catalog.palette.find(color => color.id === source.key.split(':')[1]);
          const available = variant?.available === true && Number.isSafeInteger(variant.id) && variant.id > 0 && Number.isSafeInteger(variant.price) && variant.price >= 0;
          return [source.key, { ...source, ...variant, module, color, image:this.catalog.images[source.key], available }];
        }));
        try {
          const saved = JSON.parse(sessionStorage.getItem(this.dataset.storageKey) || '[]');
          if (Array.isArray(saved)) for (const item of saved) {
            if (Array.isArray(item) && this.choices.get(item[0])?.available && Number.isSafeInteger(item[1]) && item[1] > 0 && Number.isSafeInteger(item[1] * this.choices.get(item[0]).price)) this.selection.set(item[0], item[1]);
          }
          if (!Number.isSafeInteger(this.total()) || !Number.isSafeInteger(this.count())) this.selection.clear();
        } catch { this.selection.clear(); }
        this.renderInventory();
        this.addEventListener('click', event => this.onClick(event), { signal:this.abort.signal });
        this.addEventListener('change', event => this.onChange(event), { signal:this.abort.signal });
        this.update();
        this.querySelector('[data-modules-inventory]').setAttribute('aria-busy', 'false');
      } catch (error) {
        if (error.name === 'AbortError') return;
        const inventory = this.querySelector('[data-modules-inventory]');
        inventory.textContent = this.message('load_error');
        inventory.setAttribute('aria-busy', 'false');
        this.status(this.message('load_error'), true);
      }
    }
    unitPrice(part) {
      const choice = [...this.choices.values()].find(choice => choice.module.id === part.id && Number.isSafeInteger(choice.price));
      return choice ? `${this.money(choice.price)} ${this.message(part.id === 'buttons' ? 'per_set' : 'per_each')}` : this.message('unavailable');
    }
    partNote(part) {
      return part.id === 'backplate' ? this.message('no_ring') : part.id === 'chin-long' ? this.message('extended_note') : '';
    }
    quantityControl(key, place, hidden = false) {
      const choice = this.choices.get(key);
      const label = `${choice.color?.label || ''} ${choice.module.label}`.trim();
      return `<div class="sm-modules__quantity" data-quantity-control="${key}" ${hidden ? 'hidden' : ''}><button type="button" data-decrease="${key}" aria-label="${this.escape(this.message('decrease', { name:label }))}">−</button><input type="number" inputmode="numeric" min="0" step="1" value="${this.selection.get(key) || 1}" data-quantity="${key}" data-place="${place}" aria-label="${this.escape(this.message('quantity', { name:label }))}"><button type="button" data-increase="${key}" aria-label="${this.escape(this.message('increase', { name:label }))}">+</button></div>`;
    }
    tile(key, place) {
      const choice = this.choices.get(key);
      const label = `${choice.color.label} ${choice.module.label}`;
      return `<div class="sm-modules__tile ${choice.available ? '' : 'is-unavailable'}" data-tile="${key}"><button type="button" class="sm-modules__choose" data-choose="${key}" aria-pressed="false" aria-label="${this.escape(this.message('select', { name:label }))}" ${choice.available ? '' : 'disabled'}><img src="${this.escape(choice.image)}" alt="" width="400" height="400" loading="lazy" decoding="async"><span class="sm-modules__check" aria-hidden="true">✓</span></button><span class="sm-modules__tile-hint" data-tile-hint="${key}">${this.message(choice.available ? 'select_short' : 'sold_out')}</span>${this.quantityControl(key, place, true)}</div>`;
    }
    renderInventory() {
      const parts = this.catalog.parts.filter(part => part.id !== 'magsafe');
      const colors = this.catalog.palette;
      const desktop = `<table class="sm-modules__matrix"><caption class="visually-hidden">${this.message('matrix_caption')}</caption><thead><tr><th scope="col">${this.message('module_color')}</th>${colors.map(color => `<th scope="col"><span class="sm-modules__color-dot" style="--part-color:${color.hex}"></span>${this.escape(color.label)}</th>`).join('')}</tr></thead><tbody>${parts.map((part, index) => `<tr><th scope="row"><span class="sm-modules__row-number">${String(index + 1).padStart(2,'0')}</span><strong class="sm-modules__part-label">${this.escape(part.label)}</strong><span class="sm-modules__part-price">${this.unitPrice(part)}</span>${this.partNote(part) ? `<span class="sm-modules__part-note">${this.partNote(part)}</span>` : ''}</th>${colors.map(color => `<td>${this.tile(`${part.id}:${color.id}`, 'matrix')}</td>`).join('')}</tr>`).join('')}</tbody></table>`;
      const mobile = `<div class="sm-modules__mobile-library">${parts.map(part => `<section class="sm-modules__mobile-group" aria-label="${this.escape(part.label)}"><div class="sm-modules__mobile-heading"><strong>${this.escape(part.label)}</strong><span>${this.unitPrice(part)}</span></div>${this.partNote(part) ? `<p class="sm-modules__mobile-note">${this.partNote(part)}</p>` : ''}<div class="sm-modules__mobile-grid">${colors.map(color => `<div class="sm-modules__mobile-tile-wrap">${this.tile(`${part.id}:${color.id}`, 'mobile')}<span>${this.escape(color.label)}</span></div>`).join('')}</div></section>`).join('')}</div>`;
      const ring = this.choices.get('magsafe:standard');
      this.querySelector('[data-modules-inventory]').innerHTML = desktop + mobile + `<div class="sm-modules__ring"><img src="${this.escape(ring.image)}" alt="" width="400" height="400" loading="lazy"><div class="sm-modules__ring-info"><strong>${this.escape(ring.module.label)}</strong><p>${this.message('ring_note')}</p><span>${this.unitPrice(ring.module)}</span></div><div class="sm-modules__ring-controls"><button type="button" class="sm-modules__ring-action" data-choose="magsafe:standard" ${ring.available ? '' : 'disabled'}>${this.message(ring.available ? 'add_ring' : 'sold_out')}</button>${this.quantityControl('magsafe:standard', 'ring', true)}</div></div>`;
    }
    count() {
      return [...this.selection.values()].reduce((sum, quantity) => sum + quantity, 0);
    }
    total() {
      return [...this.selection].reduce((sum, [key, quantity]) => sum + this.choices.get(key).price * quantity, 0);
    }
    change(key, quantity) {
      if (this.busy || !this.choices.get(key)?.available) return;
      if (!Number.isSafeInteger(quantity) || quantity < 0) {
        this.status(this.message('invalid_quantity'), true);
        this.update();
        return;
      }
      const before = this.selection.get(key) || 0;
      if (quantity === 0) this.selection.delete(key); else this.selection.set(key, quantity);
      if (!Number.isSafeInteger(this.total()) || !Number.isSafeInteger(this.count())) {
        if (before) this.selection.set(key, before); else this.selection.delete(key);
        this.status(this.message('invalid_quantity'), true);
      } else {
        this.status();
        try { sessionStorage.setItem(this.dataset.storageKey, JSON.stringify([...this.selection])); } catch {}
      }
      this.update();
    }
    onClick(event) {
      const button = event.target.closest('button');
      if (!button || this.busy) return;
      if (button.hasAttribute('data-modules-add')) { this.addToCart(); return; }
      if (button.dataset.choose) this.change(button.dataset.choose, this.selection.has(button.dataset.choose) ? 0 : 1);
      if (button.dataset.increase) this.change(button.dataset.increase, (this.selection.get(button.dataset.increase) || 0) + 1);
      if (button.dataset.decrease) this.change(button.dataset.decrease, (this.selection.get(button.dataset.decrease) || 0) - 1);
      if (button.dataset.remove) this.change(button.dataset.remove, 0);
    }
    onChange(event) {
      if (event.target.matches('[data-quantity]')) this.change(event.target.dataset.quantity, event.target.value.trim() === '' ? NaN : Number(event.target.value));
    }
    update() {
      for (const [key] of this.choices) {
        const quantity = this.selection.get(key) || 0;
        for (const tile of this.querySelectorAll(`[data-tile="${key}"]`)) {
          tile.classList.toggle('is-selected', quantity > 0);
          tile.querySelector('[data-choose]').setAttribute('aria-pressed', quantity > 0 ? 'true' : 'false');
          tile.querySelector('[data-tile-hint]').hidden = quantity > 0;
        }
        for (const control of this.querySelectorAll(`[data-quantity-control="${key}"]`)) control.hidden = !quantity;
        for (const input of this.querySelectorAll(`[data-quantity="${key}"]`)) input.value = quantity || 1;
      }
      const ringButton = this.querySelector('[data-choose="magsafe:standard"]');
      ringButton.hidden = this.selection.has('magsafe:standard');
      const signature = [...this.selection.keys()].join('|');
      if (signature !== this.summarySignature) {
        this.summarySignature = signature;
        const html = [...this.selection].map(([key, quantity]) => {
          const choice = this.choices.get(key);
          return `<li class="sm-modules__selection-item" data-selection-key="${key}"><img src="${this.escape(choice.image)}" alt="" width="400" height="400"><div class="sm-modules__item-copy"><strong>${this.escape(choice.module.label)}</strong>${choice.color ? `<span><i class="sm-modules__color-dot" style="--part-color:${choice.color.hex}"></i>${this.escape(choice.color.label)}</span>` : ''}<small>${this.money(choice.price)} ${this.message(choice.module.id === 'buttons' ? 'per_set' : 'per_each')}</small></div><button type="button" class="sm-modules__remove" data-remove="${key}" aria-label="${this.escape(this.message('remove', { name:`${choice.color?.label || ''} ${choice.module.label}`.trim() }))}">×</button><div class="sm-modules__item-bottom">${this.quantityControl(key,'summary')}<strong data-line-total="${key}">${this.money(choice.price * quantity)}</strong></div></li>`;
        }).join('');
        this.querySelector('[data-modules-selection]').innerHTML = html ? `<ul class="sm-modules__selection-list">${html}</ul>` : `<p class="sm-modules__empty">${this.message('empty')}</p>`;
      }
      for (const [key, quantity] of this.selection) {
        for (const input of this.querySelectorAll(`[data-quantity="${key}"]`)) input.value = quantity;
        this.querySelector(`[data-line-total="${key}"]`).textContent = this.money(this.choices.get(key).price * quantity);
      }
      const count = this.count();
      const countText = count ? this.message(count === 1 ? 'count_one' : 'count_many', { count }) : this.message('empty_count');
      this.querySelector('[data-modules-count]').textContent = countText;
      this.querySelector('[data-modules-total]').textContent = this.money(this.total());
      this.querySelector('[data-modules-add]').disabled = this.busy || !count;
      this.querySelector('[data-mobile-summary]').hidden = !count;
      this.querySelector('[data-mobile-count]').textContent = countText;
      this.querySelector('[data-mobile-total]').textContent = this.money(this.total());
    }
    payload() {
      if (!this.selection.size) throw new Error(this.message('empty'));
      const items = [...this.selection].map(([key, quantity]) => {
        const choice = this.choices.get(key);
        if (!choice.available || !Number.isSafeInteger(quantity) || quantity < 1) throw new Error(this.message('unavailable'));
        const properties = { Model:'Titan 2 Elite', Module:choice.module.label };
        if (choice.color) properties.Color = choice.color.label;
        properties._smallsmore_mcase_module = JSON.stringify({ schemaVersion:1, sku:choice.sku, module:choice.module.id, color:choice.color || null, text:null });
        return { id:choice.id, quantity, properties };
      });
      return { items };
    }
    async addToCart() {
      if (this.busy) return;
      let payload;
      try { payload = this.payload(); } catch (error) { this.status(error.message, true); return; }
      this.busy = true;
      this.setAttribute('aria-busy', 'true');
      this.querySelector('[data-add-label]').textContent = this.message('adding');
      for (const control of this.querySelectorAll('button,input')) control.disabled = true;
      this.status(this.message('adding'));
      let successful = false;
      try {
        const response = await fetch(this.runtime.routes.cartAdd, { method:'POST', credentials:'same-origin', signal:AbortSignal.timeout(20000), headers:{ 'Content-Type':'application/json', Accept:'application/json' }, body:JSON.stringify(payload) });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(typeof result.description === 'string' ? result.description : this.message('cart_error'));
        if (!Array.isArray(result.items) || payload.items.some(line => !result.items.some(saved => Number(saved.variant_id || saved.id) === line.id && Object.entries(line.properties).every(([key,value]) => saved.properties?.[key] === value)))) throw new Error(this.message('cart_uncertain'));
        successful = true;
        try { sessionStorage.removeItem(this.dataset.storageKey); } catch {}
        this.status(this.message('added'));
        window.location.assign(this.runtime.routes.cart);
      } catch (error) {
        this.status(error.name === 'TypeError' || error.name === 'TimeoutError' || error.name === 'SyntaxError' ? this.message('cart_uncertain') : error.message, true);
      } finally {
        if (!successful) {
          this.busy = false;
          this.removeAttribute('aria-busy');
          for (const control of this.querySelectorAll('button,input')) control.disabled = false;
          for (const button of this.querySelectorAll('[data-choose]')) button.disabled = !this.choices.get(button.dataset.choose).available;
          this.querySelector('[data-add-label]').textContent = this.message('add');
          this.update();
        }
      }
    }
  }
  if (!customElements.get('mcase-modules-shop')) customElements.define('mcase-modules-shop', MCaseModulesShop);
})();
